/* eslint-disable no-param-reassign */
import { get } from 'lodash';
import numeral from 'numeral';
import React from 'react';
import storage from 'Services/storage';

export function formatPrice(
  number,
  floatingNumbers = 0,
  separatorNumber = 3,
  floatingSeparator = ',',
  numberSeparator = ' ',
) {
  if (isNaN(parseFloat(number))) {
    return '-'
  }
  const num = parseFloat(parseFloat(number)
  // eslint-disable-next-line no-bitwise
    .toFixed(Math.max(0, ~~floatingNumbers))).toString();
  let isFloatingNumber;
  if (floatingNumbers > 0 && ((num || '').toString().includes('.') || (num || '').toString().includes(','))) {
    isFloatingNumber = true;
  } else {
    isFloatingNumber = false;
  }
  const re = `\\d(?=(\\d{${separatorNumber || 3}})+${isFloatingNumber ? '\\D' : '$'})`;
  return (floatingSeparator
    ? num.replace('.', floatingSeparator)
    : num
  ).replace(new RegExp(re, 'g'), `$&${numberSeparator || ','}`);
}

export const formatShortenedPrice = (value) => numeral(value).format('0.0a');

export function getCurrencyDisplay(currency) {
  if (currency === 'HUF') {
    return 'Ft';
  }
  if (currency === 'USD') {
    return '$';
  }
  if (currency === 'EUR') {
    return '€';
  }
  if (currency === 'GBP') {
    return '£';
  }
  if (currency === 'RON') {
    return 'lei';
  }
  if (currency === 'HRK') {
    return 'kn';
  }
  if (currency === 'PLN') {
    return 'zł';
  }
  if (currency === 'CZK') {
    return 'Kč';
  }
  if (currency === 'CNY') {
    return '¥';
  }
  if (currency === 'BGN') {
    return 'лв';
  }
  if (currency === 'CAD') {
    return '$';
  }
  if (currency === 'CHF') {
    return 'Fr.';
  }
  if (currency === 'RSD') {
    return 'din';
  }
  if (currency === 'HKD') {
    return '$';
  }
  if (currency === 'JPY') {
    return '¥';
  }
  if (currency === 'SEK') {
    return 'kr';
  }
  if (currency === 'TRY') {
    return '₺';
  }
  if (currency === 'AED') {
    return 'AED';
  }
  if (currency === 'AUD') {
    return '$';
  }
  return currency;
}

export function formatVAT(vatObject) {
  return vatObject && ((Number.isNaN(parseInt(vatObject.name, 10)))
    ? `${vatObject.key}% (${vatObject.name})`
    : `${vatObject.key}%`);
}

const initialUserData = storage.getUserData();

export function getPriceDecimals(currency) {
  if (!currency) {
    return 0;
  }
  return get(initialUserData, `organization.config.CURRENCY_PRECISIONS.${currency}`, 2);
}

export function getEffectiveMoneyPrecision(currency, precision = null) {
  if (precision !== null && precision !== undefined) {
    return precision;
  }
  return getPriceDecimals(currency);
}

export function money(amount, currency, rounding, decimals = null) {
  const sym = getCurrencyDisplay(currency)
  const num = formatPrice(amount, decimals || getPriceDecimals(currency))
  const isThousand = rounding === "thousand"
  const isMillion = rounding === "million"
  if (["$", "€", "£"].includes(sym)) {
    return `${sym}${num}${isThousand ? "k" : isMillion ? "M" : ""}`
  }
  if (sym === "Ft") {
    const unit = isThousand ? "e" : (isMillion ? "m" : "")
    return `${num} ${unit}${sym}`.replace(/\s+/g, " ")
  }
  const unit = isThousand ? "k" : (isMillion ? "M" : "")
  return unit ? `${num} ${unit} ${sym}` : `${num} ${sym}`
}

export function roundNumber(number, roundTo = 2) {
  if (`${number}`.indexOf('e') !== -1) return +number.toFixed(roundTo);
  return +(`${Math.round(`${number}e+${roundTo}`)}e-${roundTo}`);
}

export function slugify(str) {
  str = str.replace(/^\s+|\s+$/g, ''); // trim
  str = str.toLowerCase();

  const replaceLookup = {
    "å": "a",
    "à": "a",
    "á": "a",
    "ã": "a",
    "ä": "a",
    "â": "a",
    "è": "e",
    "é": "e",
    "ë": "e",
    "ê": "e",
    "ì": "i",
    "í": "i",
    "ï": "i",
    "î": "i",
    "ò": "o",
    "ó": "o",
    "ö": "o",
    "ô": "o",
    "ù": "u",
    "ú": "u",
    "ü": "u",
    "ñ": "n",
    "ç": "c",
    "·": "-",
    "/": "-",
    "_": "-",
    ",": "-",
    ":": "-",
    ";": "-",
  }
  str = str.replace(new RegExp(`[${Object.keys(replaceLookup).join("")}]`, "g"), x => replaceLookup[x]);

  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-'); // collapse dashes

  return str;
}

// eslint-disable-next-line no-useless-escape
export const phoneRegEx = /(^$|^[+0-9 ]*[(]?[0-9]{1,4}[)]?[-\s\./0-9]*$)/;

export const taxNumberRegEx = /^([^0-9]*[0-9]){11}$/;
export const taxNumberRegExLight = /^([^0-9]*[0-9])+$/;

export const capitalize = (s) => {
  if (typeof s !== 'string') return '';
  if (s.toUpperCase() === s) return s;
  return s.charAt(0)
    .toUpperCase() + s.slice(1).split(' ').map((word) => word.toUpperCase() === word ? word : word.toLowerCase()).join(' ');
};

export const nonBreakableString = (string) => string.split(' ').map((substr, index, self) => (
  // eslint-disable-next-line react/jsx-filename-extension,react/no-array-index-key
  <React.Fragment key={index.toString()}>
    {substr}
    {index < self.length - 1 && (<>&nbsp;</>)}
  </React.Fragment>
));

export const search = (val, searchVal) => String(val)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .includes(
    String(searchVal)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase(),
  );

export const toKebabCase = (str) => (str || '').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
export const toCustomFieldName = (name, prefix) => {
  return prefix + '_' + slugify(name || '')
    .replace(/0/g, 'xa')
    .replace(/1/g, 'xb')
    .replace(/2/g, 'xc')
    .replace(/3/g, 'xd')
    .replace(/4/g, 'xe')
    .replace(/5/g, 'xf')
    .replace(/6/g, 'xg')
    .replace(/7/g, 'xh')
    .replace(/8/g, 'xi')
    .replace(/9/g, 'xj')
    .replace(/-/g, '')
};
